import React, { useState } from 'react';
import { AxiosError } from 'axios';

import { domainsApi, DomainConfig } from '../api/domains';
import ModeLimitBlock from './ModeLimitBlock';
import { parseModeLimitFromApiError, type ModeLimitDetail } from '../utils/modeLimitMessaging';

export type ModeLifecycleAction = 'Archive' | 'Deactivate' | 'Activate';

type ModeLifecycleActionsProps = {
    mode: DomainConfig;
    activeDomainKey?: string | null;
    onChanged: () => Promise<void> | void;
    onError?: (message: string) => void;
    onSuccess?: (action: ModeLifecycleAction, context: { wasActiveSelection: boolean }) => void;
};

export function isRetiredMode(mode: DomainConfig): boolean {
    return mode.lifecycle_status === 'archived' || mode.lifecycle_status === 'deactivated';
}

function lifecycleErrorMessage(action: string, mode: DomainConfig): string {
    return `Could not ${action.toLowerCase()} ${mode.label}. Please try again.`;
}

function parseLifecycleApiError(error: unknown, action: string, mode: DomainConfig): string {
    if (!(error instanceof AxiosError)) {
        return lifecycleErrorMessage(action, mode);
    }
    const detail = (error.response?.data as { detail?: { code?: string; message?: string } | string } | undefined)
        ?.detail;
    if (detail && typeof detail === 'object') {
        if (detail.code === 'MODE_NOT_CUSTOM') {
            return 'System modes cannot be archived, deactivated, or activated.';
        }
        if (detail.code === 'MODE_INVALID_TRANSITION' && detail.message) {
            return detail.message;
        }
        if (detail.message) {
            return detail.message;
        }
    }
    if (typeof detail === 'string' && detail.trim()) {
        return detail;
    }
    return lifecycleErrorMessage(action, mode);
}

const actionButtonClass =
    'px-3 py-1.5 rounded-md border border-theme text-[11px] font-semibold text-theme-secondary hover:text-theme hover:bg-theme-card-hover disabled:opacity-50 disabled:cursor-not-allowed';

const primaryActionButtonClass =
    'px-3 py-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 text-[11px] font-semibold text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed';

const ModeLifecycleActions: React.FC<ModeLifecycleActionsProps> = ({
    mode,
    activeDomainKey,
    onChanged,
    onError,
    onSuccess,
}) => {
    const [busyAction, setBusyAction] = useState<string | null>(null);
    const [limitDetail, setLimitDetail] = useState<ModeLimitDetail | null>(null);

    if (mode.is_system_default) return null;

    const runAction = async (
        action: ModeLifecycleAction,
        fn: () => Promise<void>,
        confirmMessage?: string,
    ) => {
        if (confirmMessage && !window.confirm(confirmMessage)) return;
        const wasActiveSelection = activeDomainKey === mode.key;
        setBusyAction(action);
        setLimitDetail(null);
        try {
            await fn();
            await onChanged();
            onSuccess?.(action, { wasActiveSelection });
        } catch (error) {
            const modeLimit = parseModeLimitFromApiError(error);
            if (modeLimit) {
                setLimitDetail(modeLimit);
                return;
            }
            onError?.(parseLifecycleApiError(error, action, mode));
        } finally {
            setBusyAction(null);
        }
    };

    if (isRetiredMode(mode)) {
        return (
            <div className="flex flex-col gap-2">
                <button
                    type="button"
                    className={primaryActionButtonClass}
                    disabled={busyAction !== null}
                    onClick={() => runAction('Activate', () => domainsApi.activateMode(mode.key))}
                    aria-label={`Activate ${mode.label}`}
                >
                    {busyAction === 'Activate' ? 'Activating...' : 'Activate'}
                </button>
                {limitDetail ? (
                    <ModeLimitBlock detail={limitDetail} context="activate" />
                ) : null}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-1">
            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    className={actionButtonClass}
                    disabled={busyAction !== null}
                    onClick={() => runAction(
                        'Archive',
                        () => domainsApi.archiveMode(mode.key),
                        `Archive ${mode.label}? Historical work stays available for filtering and review.`,
                    )}
                    aria-label={`Archive ${mode.label}`}
                    title="Archive retires this mode from active pickers"
                >
                    {busyAction === 'Archive' ? 'Archiving...' : 'Archive'}
                </button>
                <button
                    type="button"
                    className={actionButtonClass}
                    disabled={busyAction !== null}
                    onClick={() => runAction(
                        'Deactivate',
                        () => domainsApi.deactivateMode(mode.key),
                        `Deactivate ${mode.label}? Existing work stays unchanged and the mode can be activated again later.`,
                    )}
                    aria-label={`Deactivate ${mode.label}`}
                    title="Deactivate pauses this mode without implying retirement"
                >
                    {busyAction === 'Deactivate' ? 'Deactivating...' : 'Deactivate'}
                </button>
            </div>
            <p className="text-[10px] text-theme-muted max-w-xs">
                Archive retires a mode; deactivate pauses it. Historical work remains filterable in
                Workbench.
            </p>
        </div>
    );
};

export default ModeLifecycleActions;
